import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, CircularProgress, Container, FormControl, InputLabel, MenuItem, Paper, Select,
  Snackbar, Stack, Switch, Typography
} from '@mui/material';

import { AdminRoles, Role, ReminderState } from '../config/DiactiveTypes';
import { getCachedHospitals, getHospitalName } from '../services/HospitalService';
import { getAllPatients } from '../services/PatientService';
import { getDoctorReminders, updateReminderStatus } from '../services/ReminderService';
import { getAuthUserHospital } from '../services/AuthService';
import PatientsTable from '../components/patients/PatientsTable';
import RemindersPanel from '../components/patients/RemindersPanel';

const Dashboard = ({ role }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [hospitalId, setHospitalId] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewAsDoctor, setViewAsDoctor] = useState(() => searchParams.get('viewAsDoctor') === '1');
  const [doctorModeHospitalId, setDoctorModeHospitalId] = useState(() => searchParams.get('doctorHospital') ?? '');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (location.state?.successMessage) {
      setSnackbar({ open: true, message: location.state.successMessage, severity: 'success' });
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const canUseDoctorMode = role === Role.SuperUser;
  const effectiveRole = canUseDoctorMode && viewAsDoctor ? Role.Doctor : role;
  const activeHospitalId = effectiveRole === Role.Doctor
    ? (canUseDoctorMode ? doctorModeHospitalId : hospitalId)
    : hospitalId;

  useEffect(() => {
    window.scrollTo(0, 0);
    const setupDashboard = async () => {
      setLoading(true);
      if (canUseDoctorMode && viewAsDoctor && !doctorModeHospitalId) {
        setPatients([]);
        setHospitalId(null);
        setReminders([]);
        setLoading(false);
        return;
      }

      try {
        const hospitalIdOverride = canUseDoctorMode && viewAsDoctor ? doctorModeHospitalId : undefined;
        const patients = await getAllPatients(effectiveRole, { hospitalIdOverride });
        setPatients(patients);

        if (effectiveRole === Role.Doctor) {
          const resolvedHospitalId = hospitalIdOverride ?? await getAuthUserHospital();
          setHospitalId(resolvedHospitalId);

          const patientIds = patients.map((p) => p.id);
          const reminders = await getDoctorReminders(patientIds);
          const remindersWithPatientNames = reminders.map((reminder) => {
            const patient = patients.find((p) => p.id === reminder.patient);
            if (!patient) return null;
            return {
              ...reminder,
              patientName: patient.firstName ? `${patient.firstName} ${patient.lastName}` : patient.nickname
            };
          }).filter((reminder) => reminder !== null);

          setReminders(remindersWithPatientNames);
        } else {
          setHospitalId(null);
          setReminders([]);
        }
      } catch (error) {
        console.log("Error", error);
      } finally {
        setLoading(false);
      }
    };
    setupDashboard();
  }, [role, effectiveRole, canUseDoctorMode, viewAsDoctor, doctorModeHospitalId]);

  const toggleReminder = async (reminderId, completed) => {
    const reminder = reminders.find((r) => r.id === reminderId);
    if (!reminder?.patient) return;

    // Optimistically update the reminders state
    setReminders((prevReminders) =>
      prevReminders.map((r) =>
        r.id === reminderId
          ? {
              ...r,
              state: completed ? ReminderState.Completed : ReminderState.Active
            }
          : r
      )
    );

    try {
      await updateReminderStatus(reminder.patient, reminderId, completed);
    } catch (error) {
      console.error("Error updating reminder:", error);

      // Revert the UI change if the Firestore update fails
      setReminders((prevReminders) =>
        prevReminders.map((r) =>
          r.id === reminderId
            ? {
                ...r,
                state: completed ? ReminderState.Active : ReminderState.Completed
              }
            : r
        )
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={45} />
      </Box>
    );
  }

  const maxWidth = effectiveRole === Role.Doctor ? "lg" : (AdminRoles.includes(role) ? "xl" : "lg");
  const shouldShowDoctorModeHospitalSelector = canUseDoctorMode && viewAsDoctor;
  const patientNavigationState = shouldShowDoctorModeHospitalSelector
    ? { viewAsDoctor: true, hospitalIdOverride: doctorModeHospitalId }
    : {};

  return (
    <Container maxWidth={maxWidth}>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ mt: 10 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          pt: 4,
          pb: 4,
          borderRadius: 2
        }}
      >
        {canUseDoctorMode && (
          <Paper elevation={2} sx={{ mb: 2, p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={viewAsDoctor}
                  onChange={(event) => {
                    setViewAsDoctor(event.target.checked);
                    if (!event.target.checked) setDoctorModeHospitalId('');
                  }}
                  inputProps={{ 'aria-label': 'view-as-doctor-toggle' }}
                />
                <Typography variant="body1">Ver cómo médico</Typography>
              </Stack>
              {shouldShowDoctorModeHospitalSelector && (
                <FormControl size="small" sx={{ minWidth: 320 }}>
                  <InputLabel id="doctor-mode-hospital-label">Hospital</InputLabel>
                  <Select
                    labelId="doctor-mode-hospital-label"
                    value={doctorModeHospitalId}
                    label="Hospital"
                    onChange={(event) => setDoctorModeHospitalId(event.target.value)}
                  >
                    <MenuItem value="">
                      <em>Selecciona hospital</em>
                    </MenuItem>
                    {getCachedHospitals().map((hospital) => (
                      <MenuItem key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Paper>
        )}
        {activeHospitalId && (
          <Stack spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {getHospitalName(activeHospitalId)}
            </Typography>
          </Stack>
        )}
        {effectiveRole === Role.Doctor && (
          <RemindersPanel
            remindersData={reminders}
            isSinglePatient={false}
            onReminderUpdate={toggleReminder}
          />
        )}
        <Paper elevation={3} sx={{ padding: 3, pt: 2 }}>
          <PatientsTable
            patients={patients}
            role={effectiveRole}
            authUserRole={role}
            viewAsDoctor={viewAsDoctor}
            doctorModeHospitalId={doctorModeHospitalId}
            patientNavigationState={patientNavigationState}
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;